import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { ShoppingBag, DollarSign, TrendingUp, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAnalytics() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const fetchData = async () => {
      try {
        const [statsRes, salesRes] = await Promise.all([
          axios.get(`${API}/analytics/dashboard`, { headers }),
          axios.get(`${API}/analytics/sales?period=${period}`, { headers })
        ]);
        setStats(statsRes.data);
        setSalesData(salesRes.data.daily_sales || []);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token, period]);

  if (loading) {
    return (
      <AdminLayout title="Analytics" subtitle="Track your business growth and performance.">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: "Total Orders",
      value: stats?.total_orders || 0,
      change: "+23% vs last week",
      icon: ShoppingBag,
      color: "bg-blue-500/20 text-blue-400"
    },
    {
      title: "Total Revenue",
      value: `₦${(stats?.total_revenue || 0).toLocaleString()}`,
      change: "+18% vs last week",
      icon: DollarSign,
      color: "bg-green-500/20 text-green-400"
    },
    {
      title: "Avg. Order Value",
      value: `₦${(stats?.avg_order_value || 0).toLocaleString()}`,
      change: "+4.8% vs last week",
      icon: TrendingUp,
      color: "bg-purple-500/20 text-purple-400"
    },
    {
      title: "Popular Item",
      value: stats?.top_items?.[0]?.name || 'N/A',
      change: `${stats?.top_items?.[0]?.count || 0} orders`,
      icon: Star,
      color: "bg-fire-500/20 text-fire-500"
    }
  ];

  return (
    <AdminLayout title="Analytics" subtitle="Track your business growth and performance.">
      <div data-testid="admin-analytics">
        {/* Period Selector */}
        <div className="flex justify-end mb-6">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 bg-card border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="bg-card border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">{stat.title}</p>
                      <p className="text-white text-2xl font-bold">{stat.value}</p>
                      <p className="text-green-400 text-xs mt-1">{stat.change}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${stat.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Revenue Trend */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Revenue Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                {salesData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesData}>
                    <defs>
                      <linearGradient id="colorRevenue2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF4500" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FF4500" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="_id" 
                      stroke="#666"
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { weekday: 'short' });
                      }}
                    />
                    <YAxis stroke="#666" tickFormatter={(value) => `₦${(value/1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [`₦${value.toLocaleString()}`, 'Revenue']}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#FF4500"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorRevenue2)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No sales data yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Selling Items */}
          <Card className="bg-card border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Top Selling Items</CardTitle>
              <span className="text-gray-400 text-sm">View All →</span>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(stats?.top_items || []).map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      index === 1 ? 'bg-gray-400/20 text-gray-400' :
                      index === 2 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-white/10 text-white'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{item.name}</p>
                      <p className="text-gray-500 text-sm">{item.count} orders</p>
                    </div>
                    <div className="text-fire-500 font-bold">₦{(item.revenue || 0).toLocaleString()}</div>
                  </div>
                ))}
                {(!stats?.top_items || stats.top_items.length === 0) && (
                  <p className="text-gray-500 text-center py-8">No sales data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
